/**
 * Occupation Create Page
 * Page for creating new occupations
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import { OccupationForm, OccupationFormData } from './components';
import { PageShell } from '../../components';

// Services
import { occupationService, SpecialActivityType } from '../../services/occupationService';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';

interface Company {
  id: number;
  tradeName: string;
  legalName: string;
}

interface Department {
  id: number;
  name: string;
  companyId: number;
}

const OccupationCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  // Companies and departments for form
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  // Load companies and departments
  useEffect(() => {
    const loadData = async () => {
      try {
        const [companiesRes, departmentsRes] = await Promise.all([
          companyService.getCompanies({ page: 1, limit: 1000 }),
          departmentService.getDepartments({ page: 1, limit: 1000 })
        ]);
        
        const loadedCompanies = companiesRes.data.map(c => ({
          id: Number(c.id),
          tradeName: c.tradeName || c.legalName,
          legalName: c.legalName
        }));
        
        const loadedDepartments = departmentsRes.data.map(d => ({
          id: Number(d.id),
          name: d.name,
          companyId: Number(d.companyId)
        }));
        
        console.log('📋 Loaded data:', {
          companies: loadedCompanies.length,
          departments: loadedDepartments.length,
          sampleDepartment: loadedDepartments[0]
        });
        
        setCompanies(loadedCompanies);
        setDepartments(loadedDepartments);
      } catch (error) {
        console.error('Error loading data:', error);
        enqueueSnackbar('Erro ao carregar dados do formulário', { variant: 'error' });
      }
    };

    loadData();
  }, [enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = async (data: OccupationFormData) => {
    setLoading(true);
    
    try {
      console.log('💼 Form data received:', {
        ...data,
        departmentIdType: typeof data.departmentId,
        companyIdType: typeof data.companyId
      });
      
      // Validate departmentId and companyId
      if (!data.departmentId || !data.companyId) {
        enqueueSnackbar('Empresa e Departamento são obrigatórios', { variant: 'error' });
        setLoading(false);
        return;
      }
      
      // Convert specialActivities object to array
      const specialActivitiesArray: SpecialActivityType[] = [];
      if (data.specialActivities) {
        Object.entries(data.specialActivities).forEach(([key, value]) => {
          if (value === true) {
            specialActivitiesArray.push(key as SpecialActivityType);
          }
        });
      }
      
      const requestData = {
        name: data.name.trim(),
        cboCode: data.cboCode?.trim() ?? undefined,
        description: data.description?.trim() ?? undefined,
        role: data.role?.trim() ?? undefined,
        departmentId: Number(data.departmentId),
        companyId: Number(data.companyId),
        specialActivities: specialActivitiesArray,
      };
      
      console.log('📤 Sending to API:', {
        ...requestData,
        departmentIdType: typeof requestData.departmentId,
        companyIdType: typeof requestData.companyId,
        activitiesCount: specialActivitiesArray.length
      });
      
      await occupationService.createOccupation(requestData);
      
      console.log('✅ Occupation created successfully');
      enqueueSnackbar('Cargo criado com sucesso!', { variant: 'success' });
      navigate('/occupations');
    } catch (error: unknown) {
      console.error('❌ Error creating occupation:', error);
      const apiError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = (apiError.response?.data?.message || apiError.message) ?? 'Erro ao criar cargo';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/occupations');
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Cargos', path: '/occupations' },
        { label: 'Novo Cargo' },
      ]}
      title="Novo Cargo"
      subtitle="Cadastre um novo cargo organizacional no sistema"
    >
      {/* Form Card */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <OccupationForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            companies={companies}
            departments={departments}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default OccupationCreatePage;
